<script setup lang="ts">
import { capitalize } from 'lodash-es'

const params = useUrlSearchParams('history')
const route = useRoute()
const slug = route.params.slug[0]
const breadcrumbs = useState('breadcrumbs')

const config = useRuntimeConfig()
const appConfig = useAppConfig()
const blog = appConfig.blog
const siteName = config.public.siteName
const canonical = new URL(route.path, config.public.host)
const blogApi = useStoryblokBlog()
const storyblokApi = useStoryblokApiV2()

const data = await blogApi.getHome()
const author = await storyblokApi.getStory({
  by_slug: `blog/authors/${slug}`,
})

breadcrumbs.value = {
  breadcrumbs: [{ slug: `${blog.url}/`, title: capitalize(blog.url.substring(1)) }],
  title: author.name || 'Autor',
}

useHead({
  title: `${author.name} | ${siteName}`,
  link: [{
    rel: 'canonical',
    href: canonical.origin + canonical.pathname,
  }],
  meta: [{ name: 'description', content: author.content?.metaDescription || '' }],
})

const state = reactive({
  page: 1,
  articleItems: [] as StoryBlokArticle[],
  tags: data.tags as StoryBlokTagItem[],
  tagIds: '',
  loading: false,
  showFetchMore: true,
})

async function fetchByTags() {
  state.loading = true
  const newArticles = await blogApi.getArticles(state.page, { tags: { in_array: state.tagIds } })

  if (state.page === 1) state.articleItems = []
  newArticles && state.articleItems.push(...newArticles)
  state.showFetchMore = !(newArticles.length < 10)

  state.loading = false
}

async function fetchByAuthor() {
  state.loading = true
  const newArticles = await blogApi.getArticles(state.page, { authors: { all_in_array: data.value?.blogAuthor.uuid } })

  if (state.page === 1) state.articleItems = []
  newArticles && state.articleItems.push(...newArticles)
  state.showFetchMore = !(newArticles.length < 10)

  state.loading = false
}

function setTagIds(value: string) {
  state.tagIds = value?.split(',').map(x => state.tags.find(t => t.slug === x)?.uuid).join(',')
}

async function fetchMore() {
  state.page++
  if (state.tagIds)
    await fetchByTags()
  else
    await fetchByAuthor()
}

async function handleChange(value: string) {
  state.page = 1
  setTagIds(value)

  if (state.tagIds)
    await fetchByTags()
  else
    await fetchByAuthor()
}

handleChange(params.tags as string || '')
</script>

<template>
  <BlogWrapper
    :blog-homepage-items="data.homePage.content"
  >
    <div class="mt-15px">
      <div v-if="author" class="flex mb-70px">
        <div v-if="author.image.filename" class="mr-5">
          <StoryblokImage :src="author.image" width="200" class="rounded-xl shadow-passive" />
        </div>
        <div>
          <h2>{{ author.name }}</h2>
          <StoryblokRichText
            v-if="author.text"
            :document="author.text"
            class="pt-5px"
          />
        </div>
      </div>
      <h3>
        Články autora
      </h3>
      <BlogTagItems :items="state.tags" @onchange="handleChange" />
      <BlogRightPanelWrapper
        :top-articles="data.homePage.content.topArticles || []"
        :most-read-articles="data.homePage.content.mostReadArticles || []"
        :nepodcento="data.homePage.content.nepodcento[0]"
      >
        <BlogArticleItem
          v-for="(item, zz) in state.articleItems"
          :key="zz + item.slug"
          :item="item"
        />
        <div class="flex justify-center">
          <div v-if="state.loading" class="inline-flex justify-center items-center text-center text-primary text-2xl">
            <AppIcon type="fas" icon="spinner" class="fa-spin mr-2" />
          </div>

          <BaseButton v-else-if="state.showFetchMore" render="secondary" @click="fetchMore">
            {{ blog.fetchMore }}
          </BaseButton>
        </div>
      </BlogRightPanelWrapper>
      <StoryblokProductTiles
        v-editable="data.homePage.content.tiles[0].reference.content"
        v-bind="data.homePage.content.tiles[0].reference.content"
        class="py-70px md:py-140px"
      />
    </div>
  </BlogWrapper>
</template>
